import React, { useState } from 'react';
import { Check, Copy, KeyRound } from 'lucide-react';

/**
 * The one screen where a raw API key is ever visible.
 *
 * The server stores only a key's SHA-256, so the raw value exists for exactly
 * one response — the one that mints it. Every earlier version of the signup
 * flow read the account and the session out of that response and dropped the
 * key on the floor, which left people holding an account they could not call
 * the API with and no way to recover one on the Free plan. So this is
 * deliberately a stop: a step somebody has to read and dismiss, not a toast
 * that disappears while they are looking elsewhere.
 *
 * Generating in the UI no longer needs the key at all — the browser session is
 * enough — so the copy frames it as what it actually is now: the credential for
 * calling the API from outside the site.
 */
export default function NewApiKey({ apiKey, onDone, doneLabel = 'Done' }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* Clipboard blocked (insecure context, or the user said no). The key is
         on screen and selectable, so this is a convenience, not the only way. */
    }
  };

  return (
    <div data-testid="new-api-key">
      <div className="flex items-center gap-2 text-[var(--accent)]">
        <KeyRound size={16} />
        <span className="text-[0.75rem] uppercase tracking-wider">Your API key</span>
      </div>

      <h2 className="serif text-[1.5rem] leading-tight mt-3">Copy this now</h2>
      <p className="text-[0.92rem] text-[var(--text-3)] mt-2 leading-relaxed">
        This is the only time we can show it — we store a hash, never the key itself.
        You don&apos;t need it to generate skills here on the site, but you do need it to
        call the API from anywhere else.
      </p>

      <div className="mt-4 flex items-stretch gap-2">
        <code className="flex-1 bg-[var(--bg)] border border-[var(--border-2)] rounded-sm px-3 py-3 font-mono text-[0.82rem] break-all select-all">
          {apiKey.key}
        </code>
        <button
          type="button"
          onClick={copy}
          aria-label="Copy API key"
          className="shrink-0 px-3 border border-[var(--border-2)] rounded-sm text-[var(--text-3)] hover:text-[var(--text)] hover:border-[var(--accent)] transition-colors"
        >
          {copied ? <Check size={16} /> : <Copy size={16} />}
        </button>
      </div>

      <p className="text-[0.78rem] text-[var(--text-4)] mt-2">
        {apiKey.plan} plan · {apiKey.monthly_limit} calls a month. Lost it? Regenerate the key
        from your dashboard at any time.
      </p>

      <button type="button" onClick={onDone} className="btn-primary !py-3 justify-center w-full mt-5">
        {doneLabel}
      </button>
    </div>
  );
}
